// Buyer Triage Queue Page - Buyer Approval Orchestrator (PRD-BAO-001)
// Visual layout mirrors EngineeringReviewQueuePage; columns + KPIs are buyer-specific.
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Skeleton,
  Alert,
  Tooltip,
  IconButton,
  Pagination,
  Stack,
  Rating,
  alpha,
  useTheme,
  Popover,
  Checkbox,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  TrendingDown as SavingsIcon,
  RequestQuote as QuoteIcon,
  Verified as VerifiedIcon,
  Inventory as InventoryIcon,
  CheckOutlined as ApproveIcon,
  CloseOutlined as RejectIcon,
  DescriptionOutlined as QuoteRequestIcon,
  RemoveRedEyeOutlined as ViewIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  UnfoldMore as SortIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { differenceInHours } from 'date-fns';
import {
  useBuyerTriageQueue,
  useBuyerDashboard,
} from '../../hooks/useBuyerReviews';
import type {
  BuyerReview,
  BuyerReviewStatus,
  ReviewPriority,
} from '../../services/buyerReviewService';

type SortField = 'priority' | 'supplierScore' | 'confidence' | 'priceDifference' | 'savings' | 'sla' | 'createdAt';

const BuyerTriageQueuePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string[]>(['PENDING', 'IN_PROGRESS', 'AWAITING_QUOTE']);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [slaFilter, setSlaFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<SortField>('priority');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const { data: queueData, isLoading, error, refetch } = useBuyerTriageQueue({
    status: statusFilter.length > 0 ? statusFilter.join(',') : undefined,
    priority: priorityFilter.length > 0 ? priorityFilter.join(',') : undefined,
    slaBreached: slaFilter === 'breached' ? true : slaFilter === 'healthy' ? false : undefined,
    page,
    limit: 20,
    sortBy,
    sortOrder,
  });

  const { data: dashboard } = useBuyerDashboard();

  // ── Helpers ──────────────────────────────────────────────

  const getMatchTypeChipSx = (matchType?: string) => {
    switch (matchType) {
      case 'EXACT_MATCH':
        return { bg: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.dark, label: 'EXACT MATCH' };
      case 'FORM_FIT_FUNCTION':
      case 'FFF_EQUIVALENT':
        return { bg: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.dark, label: 'FFF Equivalent' };
      case 'FUNCTIONAL_ONLY':
        return { bg: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.dark, label: 'FUNCTIONAL' };
      default:
        return { bg: alpha(theme.palette.divider, 0.5), color: theme.palette.text.secondary, label: matchType?.replace(/_/g, ' ') || 'UNKNOWN' };
    }
  };

  const getStatusColor = (status: BuyerReviewStatus): 'default' | 'primary' | 'info' | 'warning' | 'success' | 'error' => {
    switch (status) {
      case 'PENDING': return 'default';
      case 'IN_PROGRESS': return 'primary';
      case 'AWAITING_QUOTE': return 'info';
      case 'AWAITING_ENGINEERING': return 'warning';
      case 'ON_HOLD': return 'default';
      case 'COMPLETED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getPriorityBorderColor = (priority: ReviewPriority) => {
    switch (priority) {
      case 'EMERGENCY':
      case 'CRITICAL': return theme.palette.error.main;
      case 'HIGH': return theme.palette.warning.main;
      case 'STANDARD': return theme.palette.warning.light;
      default: return theme.palette.divider;
    }
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 85) return theme.palette.success.main;
    if (score >= 70) return theme.palette.primary.main;
    if (score >= 50) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getSLAInfo = (review: BuyerReview) => {
    if (review.status === 'COMPLETED' || review.status === 'CANCELLED') {
      return { label: '-', severity: 'default' as const };
    }
    if (review.slaBreached) {
      return { label: 'Overdue', severity: 'error' as const };
    }
    if (!review.dueAt) {
      return { label: '-', severity: 'default' as const };
    }
    const dueDate = new Date(review.dueAt);
    if (isNaN(dueDate.getTime())) {
      return { label: '-', severity: 'default' as const };
    }
    const hoursRemaining = differenceInHours(dueDate, new Date());
    const daysRemaining = Math.ceil(hoursRemaining / 24);
    if (daysRemaining <= 0) return { label: 'Today', severity: 'error' as const };
    if (daysRemaining === 1) return { label: '1 day', severity: 'warning' as const };
    if (daysRemaining <= 3) return { label: `${daysRemaining} days`, severity: 'warning' as const };
    return { label: `${daysRemaining} days`, severity: 'default' as const };
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  // ── KPI Stats ──────────────────────────────────────────────

  const stats: Array<{
    label: string;
    value: number | string;
    suffix?: string;
    subValue?: string;
    filter?: () => void;
    active?: boolean;
  }> = [
    {
      label: 'Pending',
      value: dashboard?.triageQueue?.pending ?? 0,
      filter: () => { setStatusFilter(['PENDING']); setPage(1); },
      active: statusFilter.length === 1 && statusFilter[0] === 'PENDING',
    },
    {
      label: 'In Progress',
      value: dashboard?.triageQueue?.inProgress ?? 0,
      filter: () => { setStatusFilter(['IN_PROGRESS']); setPage(1); },
      active: statusFilter.length === 1 && statusFilter[0] === 'IN_PROGRESS',
    },
    {
      label: 'Awaiting Quote',
      value: dashboard?.triageQueue?.awaitingQuote ?? 0,
      filter: () => { setStatusFilter(['AWAITING_QUOTE']); setPage(1); },
      active: statusFilter.length === 1 && statusFilter[0] === 'AWAITING_QUOTE',
    },
    {
      label: 'Awaiting Eng.',
      value: dashboard?.triageQueue?.awaitingEngineering ?? 0,
      filter: () => navigate('/engineering/review-queue'),
    },
    {
      label: 'SLA Breached',
      value: dashboard?.slaBreached ?? 0,
      filter: () => { setSlaFilter(slaFilter === 'breached' ? 'all' : 'breached'); setPage(1); },
      active: slaFilter === 'breached',
    },
    {
      label: 'On Hold',
      value: dashboard?.onHold ?? 0,
    },
    {
      label: 'Potential Savings',
      value: `$${((dashboard?.totalPotentialSavings ?? 0) / 1000).toFixed(0)}K`,
      subValue: `${dashboard?.avgSavingsPercentage?.toFixed(0) ?? 0}% avg`,
    },
  ];

  // ── Filter Popover State ─────────────────────────────────

  const [filterAnchor, setFilterAnchor] = useState<{ el: HTMLElement; type: 'status' | 'priority' | 'sla' } | null>(null);

  const statusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'AWAITING_QUOTE', label: 'Awaiting Quote' },
    { value: 'AWAITING_ENGINEERING', label: 'Awaiting Engineering' },
    { value: 'ON_HOLD', label: 'On Hold' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];

  const priorityOptions = [
    { value: 'EMERGENCY', label: 'Emergency' },
    { value: 'CRITICAL', label: 'Critical' },
    { value: 'HIGH', label: 'High' },
    { value: 'STANDARD', label: 'Standard' },
    { value: 'LOW', label: 'Low' },
  ];

  const slaOptions = [
    { value: 'all', label: 'All' },
    { value: 'healthy', label: 'On Track' },
    { value: 'breached', label: 'Overdue' },
  ];

  const toggleStatus = (val: string) => {
    setStatusFilter((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
    setPage(1);
  };

  const togglePriority = (val: string) => {
    setPriorityFilter((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
    setPage(1);
  };

  const hasActiveFilters = statusFilter.length > 0 || priorityFilter.length > 0 || slaFilter !== 'all';

  const getFilterLabel = (type: 'status' | 'priority' | 'sla') => {
    if (type === 'status') return statusFilter.length > 0 ? `Status: ${statusFilter.length}` : 'Status';
    if (type === 'priority') return priorityFilter.length > 0 ? `Priority: ${priorityFilter.length}` : 'Priority';
    return slaFilter !== 'all' ? `SLA: ${slaFilter === 'breached' ? 'Overdue' : 'On Track'}` : 'SLA';
  };

  const isFilterActive = (type: 'status' | 'priority' | 'sla') => {
    if (type === 'status') return statusFilter.length > 0;
    if (type === 'priority') return priorityFilter.length > 0;
    return slaFilter !== 'all';
  };

  // ── Sortable Header ──────────────────────────────────────

  const SortableHeader = ({ field, label, align }: { field: SortField; label: string; align?: 'left' | 'center' | 'right' }) => {
    const isActive = sortBy === field;
    return (
      <TableCell
        align={align}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
          '&:hover': { color: theme.palette.primary.main },
        }}
        onClick={() => handleSort(field)}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          {isActive ? (
            sortOrder === 'asc' ? <ArrowUpIcon sx={{ fontSize: 14 }} /> : <ArrowDownIcon sx={{ fontSize: 14 }} />
          ) : (
            <SortIcon sx={{ fontSize: 14, opacity: 0.4 }} />
          )}
        </Box>
      </TableCell>
    );
  };

  // ── Supplier Info Cell ───────────────────────────────────

  const SupplierInfoCell = ({ review }: { review: BuyerReview }) => {
    const ratingPercent = review.supplierScorecard?.overallScore || review.confidenceScore || 85;
    const rating = ratingPercent / 20;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 200 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            border: '1px solid',
            borderColor: theme.palette.divider,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <InventoryIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }} noWrap>
              {review.supplierName || 'Unknown Supplier'}
            </Typography>
            <VerifiedIcon sx={{ fontSize: 12, color: theme.palette.primary.main }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Rating value={rating} precision={0.5} size="small" readOnly sx={{ fontSize: 11 }} />
            <Typography variant="caption" color="text.secondary">
              {ratingPercent}%
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // ── Render ──────────────────────────────────────────────

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
      {/* Header Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Buyer Triage Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Initial commercial evaluation of alternative parts
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            sx={{ textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<QuoteIcon />}
            onClick={() => navigate('/buyer/approvals')}
            sx={{ textTransform: 'none', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
          >
            Approval Queue
          </Button>
        </Stack>
      </Box>

      {/* KPI Strip */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 1.5,
          mb: 3,
        }}
      >
        {stats.map((stat) => (
          <Paper
            key={stat.label}
            elevation={0}
            onClick={stat.filter}
            sx={{
              px: 2,
              py: 1.5,
              cursor: stat.filter ? 'pointer' : 'default',
              border: '1px solid',
              borderColor: stat.active ? theme.palette.primary.main : theme.palette.divider,
              bgcolor: stat.active ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
              borderRadius: 1.5,
              transition: 'all 0.15s',
              '&:hover': stat.filter ? {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              } : {},
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {stat.value}{stat.suffix || ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {stat.label}
            </Typography>
            {stat.subValue && (
              <Typography variant="caption" sx={{ display: 'block', color: theme.palette.success.main, fontWeight: 600, mt: 0.25 }}>
                {stat.subValue}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>

      {/* Compact Filter Chips */}
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 2 }}>
        <FilterIcon sx={{ fontSize: 16, color: theme.palette.text.disabled, mr: 0.25 }} />
        {(['status', 'priority', 'sla'] as const).map((type) => (
          <Chip
            key={type}
            label={getFilterLabel(type)}
            size="small"
            variant={isFilterActive(type) ? 'filled' : 'outlined'}
            color={isFilterActive(type) ? 'primary' : 'default'}
            onClick={(e) => setFilterAnchor({ el: e.currentTarget, type })}
            onDelete={isFilterActive(type) ? () => {
              if (type === 'status') { setStatusFilter([]); }
              else if (type === 'priority') { setPriorityFilter([]); }
              else { setSlaFilter('all'); }
              setPage(1);
            } : undefined}
            sx={{
              height: 28,
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
              '& .MuiChip-label': { px: 1 },
              '& .MuiChip-deleteIcon': { fontSize: 16 },
            }}
          />
        ))}
        {hasActiveFilters && (
          <Chip
            label="Clear all"
            size="small"
            variant="outlined"
            onClick={() => { setStatusFilter(['PENDING', 'IN_PROGRESS', 'AWAITING_QUOTE']); setPriorityFilter([]); setSlaFilter('all'); setPage(1); }}
            sx={{ height: 28, fontSize: '0.7rem', color: theme.palette.text.secondary, borderStyle: 'dashed', '& .MuiChip-label': { px: 1 } }}
          />
        )}
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
          {queueData?.total ?? 0} results
        </Typography>
      </Stack>

      {/* Filter Popover */}
      <Popover
        open={!!filterAnchor}
        anchorEl={filterAnchor?.el}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 1.5, minWidth: 200, boxShadow: theme.shadows[4] } } }}
      >
        <List dense disablePadding sx={{ py: 0.5 }}>
          {filterAnchor?.type === 'status' && statusOptions.map((opt) => (
            <ListItemButton key={opt.value} dense onClick={() => toggleStatus(opt.value)} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox size="small" checked={statusFilter.includes(opt.value)} sx={{ p: 0.25 }} />
              </ListItemIcon>
              <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
            </ListItemButton>
          ))}
          {filterAnchor?.type === 'priority' && priorityOptions.map((opt) => (
            <ListItemButton key={opt.value} dense onClick={() => togglePriority(opt.value)} sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox size="small" checked={priorityFilter.includes(opt.value)} sx={{ p: 0.25 }} />
              </ListItemIcon>
              <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
            </ListItemButton>
          ))}
          {filterAnchor?.type === 'sla' && slaOptions.map((opt) => (
            <ListItemButton
              key={opt.value}
              dense
              selected={slaFilter === opt.value}
              onClick={() => { setSlaFilter(opt.value); setPage(1); setFilterAnchor(null); }}
              sx={{ py: 0.5 }}
            >
              <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
            </ListItemButton>
          ))}
        </List>
      </Popover>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
          Failed to load buyer reviews. Please try again.
        </Alert>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: theme.palette.divider, borderRadius: 2, overflow: 'hidden', bgcolor: '#f1f5f9' }}>
        {isLoading && <LinearProgress />}
        <TableContainer>
          <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: '0 8px', px: 1 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: theme.palette.text.secondary, py: 1.5, border: 0, whiteSpace: 'nowrap', bgcolor: 'transparent' } }}>
                <SortableHeader field="supplierScore" label="Supplier" />
                <TableCell>Master Part</TableCell>
                <TableCell>Alternative Part</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Match</TableCell>
                <SortableHeader field="confidence" label="Confidence" />
                <TableCell>Unit Price</TableCell>
                <SortableHeader field="priceDifference" label="Price Diff" />
                <SortableHeader field="savings" label="Savings" />
                <TableCell>Status</TableCell>
                <SortableHeader field="sla" label="SLA" />
                <TableCell align="center" sx={{ width: 140 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <TableRow key={idx}>
                    {Array.from({ length: 12 }).map((_, ci) => (
                      <TableCell key={ci}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : queueData?.data?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body2" color="text.secondary">No reviews found matching your filters</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                queueData?.data?.map((review) => {
                  const slaInfo = getSLAInfo(review);
                  const matchSx = getMatchTypeChipSx(review.matchType);
                  const confidenceScore = review.confidenceScore || 0;

                  const altPrice = review.savingsCalculation?.alternativeUnitPrice || 0;
                  const priceDiff = review.savingsCalculation?.priceDifference || 0;
                  const priceDiffPercent = review.savingsCalculation?.savingsPercentage || 0;
                  const annualSavings = review.savingsCalculation?.annualSavings || 0;

                  return (
                    <TableRow
                      key={review._id}
                      hover
                      onClick={() => navigate(`/buyer/reviews/${review._id}`)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: 'background.paper',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        transition: 'box-shadow 120ms ease, background-color 120ms ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                        },
                        '& td': { py: 1.5, border: 0 },
                        '& td:first-of-type': {
                          borderTopLeftRadius: 8,
                          borderBottomLeftRadius: 8,
                          borderLeft: '4px solid',
                          borderLeftColor: getPriorityBorderColor(review.priority),
                          pl: 2,
                        },
                        '& td:last-of-type': {
                          borderTopRightRadius: 8,
                          borderBottomRightRadius: 8,
                        },
                      }}
                    >
                      {/* Supplier (left accent stripe encodes priority) */}
                      <TableCell>
                        <Tooltip title={`Priority: ${review.priority}`} placement="right">
                          <Box>
                            <SupplierInfoCell review={review} />
                          </Box>
                        </Tooltip>
                      </TableCell>

                      {/* Master Part */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                          {review.sourcePartNumber || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 180 }}>
                          {review.masterManufacturer || ''}
                        </Typography>
                      </TableCell>

                      {/* Alternative Part */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                          {review.alternativePartNumber || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 180 }}>
                          {review.alternativeManufacturer || ''}
                        </Typography>
                      </TableCell>

                      {/* Description */}
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Tooltip title={review.partDescription || review.alternativePartNumber || ''}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.8125rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.4,
                              maxWidth: 180,
                            }}
                          >
                            {review.partDescription || review.alternativePartNumber || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* Match Type */}
                      <TableCell>
                        <Chip
                          label={matchSx.label}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            bgcolor: matchSx.bg,
                            color: matchSx.color,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </TableCell>

                      {/* Confidence */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: alpha(theme.palette.divider, 0.5), overflow: 'hidden' }}>
                            <Box sx={{ width: `${confidenceScore}%`, height: '100%', bgcolor: getConfidenceColor(confidenceScore), borderRadius: 2 }} />
                          </Box>
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 500, minWidth: 32 }}>
                            {confidenceScore}%
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Unit Price */}
                      <TableCell>
                        {altPrice > 0 ? (
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                            ${altPrice.toFixed(2)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8125rem' }}>-</Typography>
                        )}
                      </TableCell>

                      {/* Price Diff */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {priceDiff !== 0 ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.8125rem',
                              fontWeight: 600,
                              color: priceDiff < 0 ? theme.palette.success.main : theme.palette.error.main,
                            }}
                          >
                            {priceDiff < 0 ? '-' : '+'}${Math.abs(priceDiff).toFixed(2)} ({priceDiffPercent.toFixed(0)}%)
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8125rem' }}>-</Typography>
                        )}
                      </TableCell>

                      {/* Savings */}
                      <TableCell>
                        {annualSavings > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <SavingsIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
                            <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.palette.success.main }}>
                              ${annualSavings.toLocaleString()}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8125rem' }}>-</Typography>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={review.status.replace(/_/g, ' ')}
                          size="small"
                          color={getStatusColor(review.status)}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500, '& .MuiChip-label': { px: 1 } }}
                        />
                      </TableCell>

                      {/* SLA */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {slaInfo.severity === 'error' && <TimeIcon sx={{ fontSize: 14, color: theme.palette.error.main }} />}
                          {slaInfo.severity === 'warning' && <TimeIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />}
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontSize: '0.8125rem',
                              fontWeight: slaInfo.severity !== 'default' ? 600 : 400,
                              color: slaInfo.severity === 'error' ? theme.palette.error.main
                                : slaInfo.severity === 'warning' ? theme.palette.warning.dark
                                : theme.palette.text.secondary,
                            }}
                          >
                            {slaInfo.label}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.25} justifyContent="center">
                          <Tooltip title="Approve">
                            <IconButton size="small">
                              <ApproveIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton size="small">
                              <RejectIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Request Quote">
                            <IconButton size="small">
                              <QuoteRequestIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => navigate(`/buyer/reviews/${review._id}`)}>
                              <ViewIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {queueData && queueData.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={queueData.totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default BuyerTriageQueuePage;
